import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, message, Menu, Layout, Typography, Space, Button, Switch, Divider, Slider, ColorPicker, Select, Radio, Card } from 'antd';
import FontIcon from './common/FontIcon';
import './SettingsDrawer.css';
import { 
  resetAllDataThunk, 
  exportData, 
  importData, 
  importChromeBookmarks,
  setAutoFocusSearch,
  setShowSearch,
  setShowQuickSearch,
  setDockVisible,
  setSideNavAutoHide,
  setIconTextColor,
  setIconSize,
  setIconRadius,
  setIconSpacing,
  setWebsiteDisplayMode,
  setLanguage
} from '../redux/slices/settingsSlice';
import { logoutUser } from '../redux/slices/authSlice';
import storage from '../utils/LocalStorageManager';
import logger from '../utils/logger';
import trackingManager from '../services/trackingManager';
import { get } from '../services/apiClient';
import * as authService from '../services/authService';
import ExtensionInstallGuide from './popup/ExtensionInstallGuide';
import { getExtensionVersion, getExtensionInfo, versionToNumber, isExtensionEnvironment } from '../utils/extensionUtils';
import languageManager from '../utils/languageManager';
import { LANGUAGES } from '../config/languageConfig';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SettingsDrawer = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const { user, isLoggedIn } = useSelector(state => state.auth);
  const { isResetting, language } = useSelector(state => state.settings);
  
  // 添加语言状态
  const [currentLanguage, setCurrentLanguage] = React.useState(language);
  
  const fileInputRef = useRef(null);
  const chromeBookmarksInputRef = useRef(null);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('profile');
  const [isAnimating, setIsAnimating] = useState(false);
  
  // 同步状态
  const [syncing, setSyncing] = useState(false);

  // 扩展程序相关状态
  const [extensionModalVisible, setExtensionModalVisible] = useState(false);
  const [extensionInfo, setExtensionInfo] = useState(null);
  const [extensionLoading, setExtensionLoading] = useState(false);

  // 获取最新扩展程序信息
  const fetchLatestExtension = async () => {
    setExtensionLoading(true);
    try {
      // 获取当前版本号并转换为数字格式（去掉点号）
      const currentVersion = getExtensionVersion();
      const currentVersionNumber = versionToNumber(currentVersion);
      
      logger.log('[SettingsDrawer] 当前版本号:', currentVersion, '转换为数字:', currentVersionNumber);
      
      const data = await get('/admin/extensions/check-update', {
        params: { currentVersion: currentVersionNumber }
      });
      
      if (data.success && data.data.hasUpdate) {
        setExtensionInfo(data.data.updateInfo);
        setExtensionModalVisible(true);
      } else {
        messageApi.info('当前已是最新版本');
      }
    } catch (error) {
      console.error('获取扩展程序信息失败:', error);
      messageApi.error('获取扩展程序信息失败，请稍后重试');
    } finally {
      setExtensionLoading(false);
    }
  };

  // 下载扩展程序
  const downloadExtension = async () => {
    if (!extensionInfo) return;
    
    try {
      const data = await get(`/admin/extensions/${extensionInfo._id}/download`);
      
      if (data.success) {
        // 创建下载链接
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        messageApi.success('扩展程序下载开始');
        setExtensionModalVisible(false);
      } else {
        messageApi.error('下载失败: ' + (data.message || '未知错误'));
      }
    } catch (error) {
      console.error('下载扩展程序失败:', error);
      messageApi.error('下载失败，请稍后重试');
    }
  };

  // 修改设置状态的获取方式
  const { 
    autoFocusSearch, 
    showSearch, 
    showQuickSearch,
    dockVisible,
    sideNavAutoHide,
    iconTextColor,
    iconSize,
    iconRadius,
    iconSpacing,
    websiteDisplayMode
  } = useSelector(state => state.settings);

  // 添加动画效果
  useEffect(() => {
    if (visible) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 400);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  // 处理设置变更
  const handleSettingChange = (key, value) => {
    switch(key) {
      case 'autoFocusSearch':
        setAutoFocusSearch(value);
        storage.setAutoFocusSearch(value);
        break;
      case 'showSearch':
        setShowSearch(value);
        storage.setShowSearch(value);
        break;
      case 'showQuickSearch':
        setShowQuickSearch(value);
        storage.setShowQuickSearch(value);
        break;
    }
    
    // 上报设置变更埋点
    trackingManager.reportSettingsChange(key, value);
    
    // 触发设置变更事件
    window.dispatchEvent(new CustomEvent('settingsChanged', {
      detail: { key, value }
    }));
  };

  // 添加语言变更监听器
  useEffect(() => {
    const unsubscribe = languageManager.addListener((newLanguage) => {
      setCurrentLanguage(newLanguage);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  // 处理语言切换
  const handleLanguageChange = (newLanguage) => {
    dispatch(setLanguage(newLanguage));
    trackingManager.reportSettingsChange('language', newLanguage);
  };

  // 菜单项配置 - 按照设计图的结构
  const menuItems = [
    {
      key: 'profile',
      icon: <FontIcon name="user" />,
      label: languageManager.t('settings.profile'),
    },
    {
      key: 'openMode',
      icon: <FontIcon name="edit" />,
      label: languageManager.t('settings.openMode'),
    },
    {
      key: 'theme',
      icon: <FontIcon name="palette" />,
      label: languageManager.t('settings.theme'),
    },
    {
      key: 'extension',
      icon: <FontIcon name="puzzle-piece" />,
      label: languageManager.t('settings.extension'),
    },
    {
      key: 'about',
      icon: <FontIcon name="info-circle" />,
      label: languageManager.t('settings.about'),
    },
  ];

  // 重置图标数据
  const showResetConfirm = () => {
    setResetModalVisible(true);
  };

  const handleResetData = async () => {
    onClose();
    setResetModalVisible(false)
    const hide = messageApi.loading('正在重置数据...', 0);
    try {
      hide();
      await dispatch(resetAllDataThunk()).unwrap();
          messageApi.success({
            content: '数据重置成功，页面将在1秒后刷新',
            icon: <FontIcon name="rocket" className="success-icon" />,
          });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      hide();
      messageApi.error('重置数据失败: ' + error);
      logger.error('重置数据失败:', error);
    }
  };

  // 导出图标数据
  const handleExportIcons = () => {
    try {
      dispatch(exportData()).then(action => {
        if (action.payload) {
          const exportData = action.payload;
          
          const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `desktop-icons-${new Date().toISOString().slice(0,10)}.json`;
          document.body.appendChild(link);
          link.click();
          
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          messageApi.success({
            content: '数据导出成功！',
            icon: <FontIcon name="star" className="success-icon" />,
          });
        }
      }).catch(error => {
        logger.error('导出失败:', error);
        messageApi.error('导出失败，请重试');
      });
    } catch (error) {
      logger.error('导出失败:', error);
      messageApi.error('导出失败，请重试');
    }
  };

  // 导入图标数据
  const handleImportIcons = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        if (!importedData.navItems) {
          throw new Error('无效的数据格式');
        }

        const hide = messageApi.loading('正在导入数据...', 0);

        try {
          await dispatch(importData(importedData)).unwrap();
          
          hide();
          onClose();

          messageApi.success({
            content: '数据导入成功，页面将在1秒后刷新',
            icon: <FontIcon name="heart" className="success-icon" />,
          });
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (syncError) {
          hide();
          logger.error('导入或同步失败:', syncError);
          messageApi.error('导入失败: ' + (syncError.message || '未知错误'));
        }
      } catch (error) {
        logger.error('导入失败:', error);
        messageApi.error('导入失败，请确保文件格式正确');
      }
    };
    reader.readAsText(file);
  };

  // 导入Chrome书签
  const handleImportChromeBookmarks = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 检查文件类型
    if (!file.name.toLowerCase().endsWith('.html')) {
      messageApi.error('请选择Chrome导出的HTML书签文件');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const htmlContent = e.target.result;
        
        const hide = messageApi.loading('正在导入Chrome书签...', 0);

        try {
          const result = await dispatch(importChromeBookmarks(htmlContent)).unwrap();
          
          hide();
          onClose();

          messageApi.success({
            content: `Chrome书签导入成功！导入了${result.importedFolders}个文件夹，${result.importedBookmarks}个书签，页面将在1秒后刷新`,
            icon: <FontIcon name="heart" className="success-icon" />,
          });
          setTimeout(() => {
            // window.location.reload();
          }, 1000);
        } catch (syncError) {
          hide();
          logger.error('导入Chrome书签失败:', syncError);
          messageApi.error('导入Chrome书签失败: ' + (syncError || '未知错误'));
        }
      } catch (error) {
        logger.error('读取Chrome书签文件失败:', error);
        messageApi.error('读取Chrome书签文件失败，请确保选择了正确的HTML文件');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  // 触发文件选择
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  // 触发Chrome书签导入
  const handleImportChromeBookmarksClick = () => {
    chromeBookmarksInputRef.current?.click();
  };

  // 处理登录/登出点击
  const handleLoginClick = () => {
    if (isLoggedIn) {
      setLogoutModalVisible(true);
    } else {
      // 上报用户登录埋点
      trackingManager.reportUserAuth('LOGIN');
      onClose();
      window.dispatchEvent(new CustomEvent('showLoginModal'));
    }
  };

  const handleLogoutConfirm = () => {
    logger.log("[SettingsDrawer] 开始执行登出...");
    
    // 上报用户登出埋点
    trackingManager.reportUserAuth('LOGOUT', user?.email || '');
    
    dispatch(logoutUser());
    logger.log("[SettingsDrawer] 登出完成");
    
    window.dispatchEvent(new Event('authStatusChanged'));
    setLogoutModalVisible(false);
    messageApi.success({
      content: '退出登录成功'
    });
    onClose();
  };

  // 同步数据
  const handleSyncData = async () => {
    // 检查是否已登录
    if (!isLoggedIn) {
      logger.log('[SettingsDrawer] 未登录状态，提示用户登录');
      messageApi.warning('请先登录以同步数据');
      onClose();
      window.dispatchEvent(new CustomEvent('showLoginModal'));
      return;
    }
    
    logger.log('[SettingsDrawer] 开始同步数据');
    
    // 上报同步开始埋点
    trackingManager.reportSync('START', '手动同步数据');
    
    // 创建一个标志，用于检查组件是否已卸载
    let isMounted = true;
    
    // 设置同步状态
    setSyncing(true);
    
    try {
      // 调用同步服务
      logger.log('[SettingsDrawer] 调用 authService.syncData 方法');
      const result = await authService.syncData((...args) => {
        // 在dispatch前检查组件是否已卸载
        if (isMounted) {
          logger.log('[SettingsDrawer] 同步中调用 dispatch, action type:', args[0].type);
          return dispatch(...args);
        }
        logger.log('[SettingsDrawer] 组件已卸载，不执行 dispatch');
        return Promise.resolve();
      });
      
      // 检查组件是否仍然挂载
      if (!isMounted) {
        logger.log('[SettingsDrawer] 组件已卸载，不继续执行');
        return;
      }
      
      logger.log('[SettingsDrawer] 同步完成，结果:', result);
      
      if (result.success) {
        // 上报同步成功埋点
        trackingManager.reportSync('SUCCESS', '数据同步完成');
        messageApi.success('数据同步完成');
        
        // 主动触发一次页面刷新事件，确保侧边栏和主容器同步
        logger.log('[SettingsDrawer] 同步数据成功，手动触发页面刷新事件');
        setTimeout(() => {
          logger.log('[SettingsDrawer] 触发 forcePagesRefresh 事件');
          window.dispatchEvent(new CustomEvent('forcePagesRefresh'));
        }, 300); // 延迟触发，确保 Redux 状态已更新
      } else {
        // 上报同步失败埋点
        trackingManager.reportSync('FAIL', result.message || '同步失败');
        logger.log('[SettingsDrawer] 同步失败:', result.message);
        messageApi.warning(result.message);
      }
    } catch (error) {
      // 检查组件是否仍然挂载
      if (!isMounted) {
        logger.log('[SettingsDrawer] 组件已卸载，不处理错误');
        return;
      }
      
      // 上报同步失败埋点
      trackingManager.reportSync('FAIL', error.message || '同步异常');
      logger.error('[SettingsDrawer] 同步数据失败:', error);
      messageApi.error('同步数据失败，请稍后重试');
    } finally {
      // 检查组件是否仍然挂载
      if (isMounted) {
        // 重置同步状态
        setSyncing(false);
        logger.log('[SettingsDrawer] 同步状态重置为 false');
      }
    }
  };

  // 渲染个人中心内容
  const renderProfileContent = () => {
    return (
      <Space direction="vertical" size="middle" className="full-width-space">
        <div className="profile-info-card">
          <Typography.Title level={5} className="profile-title-no-margin">
            {isLoggedIn ? languageManager.t('settings.welcomeBack') : languageManager.t('settings.notLoggedIn')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {isLoggedIn 
              ? languageManager.t('settings.dataSynced')
              : languageManager.t('settings.loginToSync')
            }
          </Typography.Text>
          <div className={`profile-status-text ${isLoggedIn ? 'profile-status-logged-in' : 'profile-status-guest'}`}>
            {isLoggedIn 
              ? languageManager.t('settings.dataSyncing')
              : languageManager.t('settings.localDataOnly')
            }
          </div>
        </div>

        <div>
          <Typography.Title level={5} className="profile-title-with-icon">
            <FontIcon name="cog" />
            {languageManager.t('settings.profile')}
          </Typography.Title>
          <div className="profile-buttons-grid">
            <Button 
              type="text" 
              className={`profile-grid-button ${!isLoggedIn ? 'profile-button-disabled' : ''}`}
              onClick={handleSyncData}
              disabled={!isLoggedIn}
            >
              <FontIcon 
                name="sync-alt" 
                className={syncing ? 'rotating' : ''}
              />
              {syncing ? languageManager.t('settings.syncing') : languageManager.t('settings.sync')}
            </Button>
            <Button type="text" className="profile-grid-button" onClick={showResetConfirm}>
              <FontIcon name="redo"  />
              {languageManager.t('settings.reset')}
            </Button>
            <Button type="text" className="profile-grid-button" onClick={handleExportIcons}>
              <FontIcon name="download"  />
              {languageManager.t('settings.export')}
            </Button>
            <Button type="text" className="profile-grid-button" onClick={handleImportClick}>
              <FontIcon name="upload" />
              {languageManager.t('settings.import')}
            </Button>
            <Button type="text" className="profile-grid-button" onClick={handleImportChromeBookmarksClick}>
              <FontIcon name="chrome" />
              {languageManager.t('settings.importChromeBookmarks')}
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className="hidden-input"
          onChange={handleImportIcons}
        />
        <input
          ref={chromeBookmarksInputRef}
          type="file"
          accept=".html"
          className="hidden-input"
          onChange={handleImportChromeBookmarks}
        />
      </Space>
    );
  };

  // 修改渲染打开方式内容
  const renderOpenModeContent = () => {
    return (
      <Space direction="vertical" size="small" className="full-width-space">
        <div className="settings-item">
          <div>
            <Text strong className="settings-text-small">{languageManager.t('settings.autoFocusSearch')}</Text>
          </div>
          <Switch 
            checked={autoFocusSearch}
            onChange={(checked) => {
              dispatch(setAutoFocusSearch(checked));
              storage.setAutoFocusSearch(checked);
              // 上报设置变更埋点
              trackingManager.reportSettingsChange('autoFocusSearch', checked);
              // 触发设置变更事件
              window.dispatchEvent(new CustomEvent('settingsChanged', {
                detail: { key: 'autoFocusSearch', value: checked }
              }));
            }}
            size="default" 
          />
        </div>
        
        <div className="settings-item">
          <div>
            <Text strong className="settings-text-small">{languageManager.t('settings.showSearch')}</Text>
          </div>
          <Switch 
            checked={showSearch}
            onChange={(checked) => {
              dispatch(setShowSearch(checked));
              storage.setShowSearch(checked);
              // 上报设置变更埋点
              trackingManager.reportSettingsChange('showSearch', checked);
              // 触发设置变更事件
              window.dispatchEvent(new CustomEvent('settingsChanged', {
                detail: { key: 'showSearch', value: checked }
              }));
            }}
            size="default" 
          />
        </div>
        
        <div className="settings-item">
          <div>
            <Text strong className="settings-text-small">{languageManager.t('settings.showQuickSearch')}</Text>
          </div>
          <Switch 
            checked={showQuickSearch}
            onChange={(checked) => {
              dispatch(setShowQuickSearch(checked));
              storage.setShowQuickSearch(checked);
              // 上报设置变更埋点
              trackingManager.reportSettingsChange('showQuickSearch', checked);
              // 触发设置变更事件
              window.dispatchEvent(new CustomEvent('settingsChanged', {
                detail: { key: 'showQuickSearch', value: checked }
              }));
            }}
            size="default" 
          />
        </div>
      </Space>
    );
  };

  // 渲染主题标签内容
  const renderThemeContent = () => (
    <Space direction="vertical" size="middle" className="full-width-space">
      <div>
        <Title level={5} className="settings-title-small-margin">{languageManager.t('settings.theme')}</Title>
        <Space direction="vertical" size="small" className="full-width-space">
          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.displayMode')}</Text>
            <Radio.Group 
              value={websiteDisplayMode} 
              onChange={(e) => {
                dispatch(setWebsiteDisplayMode(e.target.value));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('websiteDisplayMode', e.target.value);
              }}
              size="small"
            >
              <Radio value="compact">{languageManager.t('settings.compactDisplay')}</Radio>
              <Radio value="detailed">{languageManager.t('settings.detailedDisplay')}</Radio>
            </Radio.Group>
          </div>

          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.iconSize')}</Text>
            <Slider 
              min={40} 
              max={120} 
              value={iconSize} 
              onChange={(value) => {
                dispatch(setIconSize(value));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('iconSize', value);
              }} 
              className="settings-slider"
            />
          </div>

          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.iconRadius')}</Text>
            <Slider 
              min={0} 
              max={24} 
              value={iconRadius} 
              onChange={(value) => {
                dispatch(setIconRadius(value));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('iconRadius', value);
              }} 
              className="settings-slider"
            />
          </div>

          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.iconSpacing')}</Text>
            <Slider 
              min={8} 
              max={40} 
              value={iconSpacing} 
              onChange={(value) => {
                dispatch(setIconSpacing(value));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('iconSpacing', value);
              }} 
              className="settings-slider"
            />
          </div>

          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.iconTextColor')}</Text>
            <ColorPicker 
              value={iconTextColor} 
              onChange={(color) => {
                const colorValue = color.toHexString();
                dispatch(setIconTextColor(colorValue));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('iconTextColor', colorValue);
              }}
              getPopupContainer={(triggerNode) => {
                // 确保弹出层挂载到 body，避免被父元素的 overflow 或 z-index 影响
                return document.body;
              }}
            />
          </div>
        </Space>
      </div>

      <div>
        <Title level={5} className="settings-title-small-margin">{languageManager.t('settings.theme')}</Title>
        <Space direction="vertical" size="small" className="full-width-space">
          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.dockVisible')}</Text>
            <Switch 
              checked={dockVisible} 
              onChange={(checked) => {
                dispatch(setDockVisible(checked));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('dockVisible', checked);
              }} 
            />
          </div>
          
          <div className="settings-item">
            <Text strong className="settings-text-medium">{languageManager.t('settings.sideNavAutoHide')}</Text>
            <Switch 
              checked={sideNavAutoHide} 
              onChange={(checked) => {
                dispatch(setSideNavAutoHide(checked));
                // 上报设置变更埋点
                trackingManager.reportSettingsChange('sideNavAutoHide', checked);
              }} 
            />
          </div>
        </Space>
      </div>

      <div>
        <Title level={5} className="settings-title-small-margin">语言/Language</Title>
        <Space direction="vertical" size="small" className="full-width-space">
          <div className="settings-item">
            <Text strong className="settings-text-medium">语言/Language</Text>
            <Select
              value={currentLanguage}
              onChange={handleLanguageChange}
              className="settings-select-width"
              size="default"
            >
              <Option value={LANGUAGES.ZH}>中文</Option>
              <Option value={LANGUAGES.EN}>English</Option>
            </Select>
          </div>
        </Space>
      </div>
    </Space>
  );

  // 渲染扩展程序内容
  const renderExtensionContent = () => (
    <Space direction="vertical" size="large" className="full-width-space">
      {/* 扩展程序下载 */}
      <div className="extension-download-card">
        <div className="extension-download-icon">
          🧩
        </div>
        
        <div className="extension-download-content">
          <div className="extension-download-title">
            {languageManager.t('settings.browserExtension')}
          </div>
          <div className="extension-download-desc">
            {languageManager.t('settings.setAsNewTab')}
          </div>
          <div className="extension-download-version">
            {languageManager.t('settings.currentVersion')}: v{getExtensionVersion()} • {getExtensionInfo().environment === 'extension' ? languageManager.t('settings.extensionEnvironment') : languageManager.t('settings.webEnvironment')}
          </div>
          
          <Button
            type="primary"
            size="large"
            onClick={fetchLatestExtension}
            loading={extensionLoading}
            className="extension-download-button"
            icon={<FontIcon name="download" />}
          >
            {languageManager.t('settings.getLatestVersion')}
          </Button>
        </div>
      </div>

      {/* 扩展程序功能说明 */}
      <Card
        title={
          <div className="extension-card-title">
            <FontIcon name="star" className="extension-card-title-icon" />
            <span>{languageManager.t('settings.extensionFeatures')}</span>
          </div>
        }
        className="extension-card"
      >
        <div className="extension-feature-grid">
          <div className="extension-feature-item">
            <strong>🏠 {languageManager.t('settings.newTabNavigation')}</strong>
          </div>
          
          <div className="extension-feature-item">
            <strong>⚡ {languageManager.t('settings.offlineQuickAccess')}</strong>
          </div>
          
          <div className="extension-feature-item">
            <strong>🎨 {languageManager.t('settings.personalizedCustomization')}</strong>
          </div>
          
          <div className="extension-feature-item">
            <strong>🔄 {languageManager.t('settings.dataSync')}</strong>
          </div>
          
          <div className="extension-feature-item">
            <strong>🔒 {languageManager.t('settings.privacyProtection')}</strong>
          </div>
        </div>
      </Card>

      {/* 支持浏览器 */}
      <Card
        title={
          <div className="extension-card-title">
            <FontIcon name="globe" className="extension-card-title-globe" />
            <span>{languageManager.t('settings.supportedBrowsers')}</span>
          </div>
        }
        className="extension-card"
      >
        <div className="browser-list">
          <div className="browser-item">
            <div className="browser-icon">🌐</div>
            <div className="browser-name">Chrome</div>
          </div>
          <div className="browser-item">
            <div className="browser-icon">🦊</div>
            <div className="browser-name">Firefox</div>
          </div>
          <div className="browser-item">
            <div className="browser-icon">⚡</div>
            <div className="browser-name">Edge</div>
          </div>
          <div className="browser-item">
            <div className="browser-icon">🦁</div>
            <div className="browser-name">Safari</div>
          </div>
        </div>
      </Card>
    </Space>
  );

  // 渲染关于我们内容
  const renderAboutContent = () => {
    // 创建菜单项的通用样式
    const menuItemStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '12px 14px',
      borderRadius: '8px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textAlign: 'left',
      height: 'auto',
      marginBottom: '1px',
    };

    // 创建分组容器的样式
    const groupStyle = {
      backgroundColor: 'rgba(248, 250, 252, 0.8)',
      borderRadius: '10px',
      padding: '4px',
      marginBottom: '16px',
    };
    
    // 次要分组容器样式
    const secondaryGroupStyle = {
      backgroundColor: 'rgba(248, 250, 252, 0.5)',
      borderRadius: '10px',
      padding: '3px',
      marginBottom: '12px',
    };

    // 重用菜单项创建函数
    const MenuItem = ({ label, onClick, secondary = false }) => (
      <Button 
        type="text"
        onClick={onClick}
        style={{
          ...menuItemStyle,
          ...(secondary ? {} : { boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px' })
        }}
        block
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = secondary ? 'rgba(0, 0, 0, 0.02)' : 'rgba(0, 0, 0, 0.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span style={{ 
          fontSize: secondary ? '13px' : '15px', 
          fontWeight: secondary ? '400' : '600', 
          color: secondary ? '#777777' : '#333333' 
        }}>
          {label}
        </span>
        <FontIcon 
          name="external-link-alt" 
          style={{ 
            fontSize: secondary ? '13px' : '14px', 
            color: secondary ? '#AAAAAA' : '#999999' 
          }} 
        />
      </Button>
    );

    // 打开HTML页面的通用函数
    const openHtmlPage = (path) => {
      // 移除路径开头的斜杠（如果有）
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      
      // 在扩展环境中，使用 chrome.runtime.getURL() 获取正确的路径
      if (isExtensionEnvironment() && typeof window.chrome !== 'undefined' && window.chrome.runtime && window.chrome.runtime.getURL) {
        const url = window.chrome.runtime.getURL(cleanPath);
        return window.open(url, '_blank');
      }
      
      // 在网页环境中，使用相对路径
      return window.open(path, '_blank');
    };

    return (
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        {/* 微信公众号二维码 */}
        <div className="wechat-qrcode-container">
          <div className="wechat-qrcode-title">
            <span>📱</span>
            <span>{languageManager.t('settings.followWeChat') || '关注微信公众号'}</span>
          </div>
          <div className="wechat-qrcode-desc">
            {languageManager.t('settings.wechatDesc') || '实时获取新功能推送、AI技巧及工具分享'}
          </div>
          <div className="wechat-qrcode-wrapper">
            <img 
              src={require('../assets/wechat_gzh.jpg')}
              alt="微信公众号二维码"
              className="wechat-qrcode-image"
            />
          </div>
        </div>

        {/* 第一组：更新日志、用户反馈、常见问题 */}
        <div style={groupStyle}>
          <MenuItem
            label={languageManager.t('settings.updateLog')}
            onClick={() => {
              openHtmlPage('/changelog.html');
            }}
          />
          <MenuItem
            label={languageManager.t('settings.userFeedback')}
            onClick={() => {
              window.open('https://qcno5lsrlfta.feishu.cn/share/base/form/shrcnihPh2zL4mfppFm1shcinmb', '_blank');
            }}
          />
{/* 暂时屏蔽常见问题入口
          <MenuItem
            label={languageManager.t('settings.faq')}
            onClick={() => {
              messageApi.info('常见问题功能开发中...');
            }}
          />
*/}
        </div>

        {/* 第二组：应用官网、隐私政策、免责声明 */}
        <div style={secondaryGroupStyle}>
          {/* <MenuItem
            label={languageManager.t('settings.officialWebsite')}
            onClick={() => {
              window.open('https://www.aidengta.cn', '_blank');
            }}
            secondary
          /> */}
          <MenuItem
            label={languageManager.t('settings.privacyPolicy')}
            onClick={() => {
              openHtmlPage('/privacy-policy.html');
            }}
            secondary
          />
          <MenuItem
            label={languageManager.t('settings.disclaimer')}
            onClick={() => {
              openHtmlPage('/disclaimer.html');
            }}
            secondary
          />
        </div>

        {/* 版本信息 */}
        <div style={{ 
          textAlign: 'center',
          padding: '12px 0',
          marginTop: '8px'
        }}>
          <Text type="secondary" style={{ fontSize: '12px', color: '#999999' }}>
            {languageManager.t('settings.appName')} v{getExtensionVersion()}
          </Text>
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px', color: '#999999' }}>
              粤ICP备2026024083号-1
            </Text>
          </div>
        </div>
      </Space>
    );
  };

  // 根据选中菜单渲染内容
  const renderContent = () => {
    switch (selectedMenu) {
      case 'profile':
        return renderProfileContent();
      case 'openMode':
        return renderOpenModeContent();
      case 'theme':
        return renderThemeContent();
      case 'extension':
        return renderExtensionContent(); // 扩展程序内容也放在这里
      case 'about':
        return renderAboutContent();
      default:
        return renderProfileContent();
    }
  };

  const renderHeader = () => {
    return (
      <div className="settings-header">
        <div className="header-left">
          <div className="user-info">
            <div className={`user-avatar ${isLoggedIn ? '' : 'guest'}`}>
              <img 
                src={require('../assets/logo192.png')} 
                alt="user avatar" 
                className="user-avatar-img"
              />
            </div>
            <div className="user-details">
              <div className="user-name">
                {isLoggedIn ? (user?.nickname || user?.email || languageManager.t('common.user')) : languageManager.t('settings.notLoggedIn')}
              </div>
              <div className="user-status">
                {isLoggedIn ? languageManager.t('settings.loggedIn') : languageManager.t('settings.guest')}
              </div>
            </div>
          </div>
        </div>
        <div className="header-right">
          <Button 
            type="text" 
            onClick={handleLoginClick}
            className="header-button"
          >
            <FontIcon 
              name={isLoggedIn ? "sign-out-alt" : "sign-in-alt"} 
              className="header-button-icon"
            />
            {isLoggedIn ? languageManager.t('settings.logout') : languageManager.t('settings.login')}
          </Button>
          <Button className="close-button" onClick={onClose}>
            <div className="close-icon"></div>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <div className="modal-title-wrapper">
            <span className="modal-title-icon">⚠️</span>
            <span>{languageManager.t('common.confirmReset')}</span>
          </div>
        }
        open={resetModalVisible}
        onOk={handleResetData}
        onCancel={() => setResetModalVisible(false)}
        okText={languageManager.t('common.confirmReset')}
        cancelText={languageManager.t('common.cancel')}
        zIndex={2300}
        centered
        okButtonProps={{ 
          danger: true,
          style: { borderRadius: '8px', fontWeight: '600' }
        }}
        cancelButtonProps={{ 
          style: { borderRadius: '8px', fontWeight: '600' }
        }}
      >
        <div className="modal-content-wrapper">
          <Text className="modal-text">
            {languageManager.t('common.confirmResetText')}
          </Text>
        </div>
      </Modal>
      
      <Modal
        title={
          <div className="modal-title-wrapper">
            <span>{languageManager.t('common.confirmLogout')}</span>
          </div>
        }
        open={logoutModalVisible}
        onOk={handleLogoutConfirm}
        onCancel={() => setLogoutModalVisible(false)}
        okText={languageManager.t('common.confirmLogout')}
        cancelText={languageManager.t('common.cancel')}
        zIndex={2300}
        centered
        okButtonProps={{ 
          style: { borderRadius: '8px', fontWeight: '600' }
        }}
        cancelButtonProps={{ 
          style: { borderRadius: '8px', fontWeight: '600' }
        }}
      >
        <div className="modal-content-wrapper">
          <Text className="modal-text">
            {languageManager.t('common.confirmLogoutText')}
          </Text>
        </div>
      </Modal>

      {/* 扩展程序安装指导弹窗 */}
      
      <ExtensionInstallGuide
        visible={extensionModalVisible}
        onCancel={() => setExtensionModalVisible(false)}
        onDownload={downloadExtension}
        extensionInfo={extensionInfo}
      />
      
      <div className={`settings-drawer-overlay ${visible ? 'visible' : ''}`} onClick={onClose}>
        <div className="settings-drawer" onClick={e => e.stopPropagation()}>
          {renderHeader()}
          
          {/* 主体内容 */}
          <Layout className="settings-layout">
            <Sider className="settings-sider">
              <Menu
                mode="vertical"
                selectedKeys={[selectedMenu]}
                items={menuItems}
                onClick={({ key }) => setSelectedMenu(key)}
              />
            </Sider>
            <Content className="settings-content">
              {renderContent()}
            </Content>
          </Layout>
        </div>
      </div>
    </>
  );
};

export default SettingsDrawer; 